import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const durationUnits = [
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
];

const SubCategoryCreation = () => {
  const [name, setName] = useState('');
  const location = useLocation();
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get("categoryId");
  const subcategoryId = searchParams.get("subcategoryId");
  
  const [description, setDescription] = useState('');
  const [owners, setOwners] = useState([]);
  const [ownersOptions, setOwnersOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [slas, setSlas] = useState([
    { duration: { value: '', unit: 'hours' }, escalations: [{ duration: { value: '', unit: 'hours' }, user: null }] },
  ]);
  const [loading, setLoading] = useState(false);

  const baselineRef = useRef(null); 

  const handleOwnersChange = (opts) => setOwners(opts || []);

  const convertToMinutes = (value, unit) => {
    const val = Number(value);
    if (isNaN(val) || val <= 0) return 0;
    switch (unit) {
      case 'hours': return val * 60;
      case 'days': return val * 60 * 24;
      case 'months': return val * 60 * 24 * 30;
      default: return val;
    }
  };

  const buildSubmitPayload = () => {
    const sla = slas[0];
    const slaMinutes = convertToMinutes(sla.duration.value, sla.duration.unit);

    const payloadOwners = owners.map(o => ({
      id: (o.id && o.id !== o.notifyUserId) ? o.id : null,
      notifyUserId: o.notifyUserId || o.value,
      notifyRole: o.notifyRole || null,
    }));

    const payloadEscalations = (sla.escalations || []).map((esc, idx) => ({
      id: esc.id || null,
      escalationLevel: idx + 1,
      triggerMinutes: convertToMinutes(esc.duration.value, esc.duration.unit),
      notifyUserId: esc.user?.notifyUserId || esc.user?.value || null,
      notifyRole: esc.user?.notifyRole || null,
    }));

    return {
      id: subcategoryId || null,
      categoryId,
      name,
      description,
      slaMinutes,
      owners: payloadOwners,
      escalations: payloadEscalations,
    };
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_EMPLOYEE_LIST, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch employees');

        const result = await response.json();
        if (result.success && result.data?.employees) {
          const options = result.data.employees.map(emp => ({
            value: emp.id,
            label: emp.name,
            notifyUserId: emp.id,
            notifyRole: emp.role || null,
            id: emp.id,
          }));
          setOwnersOptions(options);
          setUserOptions(options);
        } else {
          console.error('No employees data found');
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!categoryId || !subcategoryId) {
      baselineRef.current = buildSubmitPayload();
      return;
    }

    const fetchSubcategory = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_SUBCATEGORY_LIST}?categoryId=${categoryId}`, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch subcategories');

        const result = await response.json();

        if (result.success && result.data.subcategories.length > 0) {
          const subcat = result.data.subcategories.find(
            sc => String(sc.id) === String(subcategoryId)
          );          
          if (!subcat) throw new Error('Subcategory not found');

          setName(subcat.name || '');
          setDescription(subcat.description || '');

          const ownerOpts = (subcat.owners || []).map(o => ({
            value: o.notifyUserId,
            label: o.notifyUserName,
            notifyUserId: o.notifyUserId,
            notifyRole: o.notifyRole || null,
            id: o.id || null,
          }));
          setOwners(ownerOpts);

          const slaDurationHours = (subcat.slaMinutes || 0) / 60;
          const escalations = (subcat.escalations || []).map(e => ({
            duration: { value: (e.triggerMinutes || 0) / 60, unit: 'hours' },
            user:
              userOptions.find(o => o.notifyUserId === e.notifyUserId) ||
              (e.notifyUserId
                ? { value: e.notifyUserId, label: e.notifyUserName || `User ${e.notifyUserId}`, notifyUserId: e.notifyUserId, notifyRole: e.notifyRole || null, id: e.notifyUserId }
                : null),
            id: e.id || null,
          }));
          setSlas([{ duration: { value: slaDurationHours, unit: 'hours' }, escalations: escalations.length ? escalations : [{ duration: { value: '', unit: 'hours' }, user: null }] }]);

          const baseOwners = (subcat.owners || []).map(o => ({
            id: o.id || null,
            notifyUserId: o.notifyUserId,
            notifyRole: o.notifyRole || null,
          }));
          const baseEscalations = (subcat.escalations || []).map((e, idx) => ({
            id: e.id || null,
            escalationLevel: idx + 1,
            triggerMinutes: e.triggerMinutes || 0,
            notifyUserId: e.notifyUserId || null,
            notifyRole: e.notifyRole || null,
          }));
          baselineRef.current = {
            id: subcategoryId,
            categoryId,
            name: subcat.name || '',
            description: subcat.description || '',
            slaMinutes: subcat.slaMinutes || 0,
            owners: baseOwners,
            escalations: baseEscalations,
          };
        }
      } catch (error) {
        console.error('Error fetching subcategory:', error);
      }
    };

    fetchSubcategory();
  }, [categoryId, subcategoryId, userOptions]);

  const handleSLADurationChange = (idx, field, val) => {
    const newSlas = [...slas];
    if (field === 'value') newSlas[idx].duration.value = val;
    else newSlas[idx].duration.unit = val;
    setSlas(newSlas);
  };

  const handleEscDurationChange = (slaIdx, escIdx, field, val) => {
    const newSlas = [...slas];
    if (field === 'value') newSlas[slaIdx].escalations[escIdx].duration.value = val;
    else newSlas[slaIdx].escalations[escIdx].duration.unit = val;
    setSlas(newSlas);
  };

  const handleEscUserChange = (slaIdx, escIdx, opt) => {
    const newSlas = [...slas];
    newSlas[slaIdx].escalations[escIdx].user = opt;
    setSlas(newSlas);
  };

  const addSLA = () => {
    setSlas([
      ...slas,
      { duration: { value: '', unit: 'hours' }, escalations: [{ duration: { value: '', unit: 'hours' }, user: null }] },
    ]);
  };

  const removeSLA = (idx) => setSlas(slas.filter((_, i) => i !== idx));

  const addEscalation = (slaIdx) => {
    const newSlas = [...slas];
    newSlas[slaIdx].escalations.push({ duration: { value: '', unit: 'hours' }, user: null });
    setSlas(newSlas);
  };

  const removeEscalation = (slaIdx, escIdx) => {
    const newSlas = [...slas];
    newSlas[slaIdx].escalations = newSlas[slaIdx].escalations.filter((_, i) => i !== escIdx);
    setSlas(newSlas);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Description is required before submitting.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const newPayload = buildSubmitPayload();
      const oldPayload = baselineRef.current || {};

      if (subcategoryId) {
        const { digest, html } = buildDiffPreview(oldPayload, newPayload);
        if (!digest.length) {
          await Swal.fire({
            icon: 'info',
            title: 'Nothing to update',
            text: 'No differences detected.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2500,
          });
          setLoading(false);
          return;
        }
        const confirm = await Swal.fire({
          title: 'Apply these changes?',
          html,
          showCancelButton: true,
          confirmButtonText: 'Update',
        });
        if (!confirm.isConfirmed) {
          setLoading(false);
          return;
        }
      }

      const response = await fetch(process.env.REACT_APP_API_SUBCATEGORY_CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(newPayload),
      });

      const respJson = await response.json();
      if (!response.ok || !respJson.success) {
        throw new Error(respJson.success_message || respJson.message || 'Something went wrong');
      }

      fireAudit({
        entity: 'subcategory',
        affected_id: subcategoryId || null,
        action: subcategoryId ? 'update' : 'create',
        businessApi: process.env.REACT_APP_API_SUBCATEGORY_CREATE,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        section: 'Ticket',
        subSection: 'Subcategory Creation',
        extra: { page: 'SubCategoryCreation' },
      });

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'SubCategory saved successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = newPayload;

      setName('');
      setDescription('');
      setOwners([]);
      setSlas([{ duration: { value: '', unit: 'hours' }, escalations: [{ duration: { value: '', unit: 'hours' }, user: null }] }]);
      navigate(-1);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to save SubCategory.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <span
          className="breadcrumb-link"
          style={{ cursor: 'pointer', color: '#007bff', marginRight: 4 }}
          onClick={() => navigate(-1)}
        >
          Subcategory List
        </span>
        {' '} &gt; {subcategoryId ? 'Update Subcategory' : 'Subcategory Creation'}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div className="floating-select-form">
          <input
            id="subcategory-name"
            name="name"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Name"
            required
          />
          {name && <label htmlFor="subcategory-name">Name</label>}
        </div>

        {/* Description */}
        <div className="floating-select-form">
          <textarea
            id="subcategory-description"
            name="description"
            className={`walkin-form-comments ${description ? 'has-value' : ''}`}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description"
            rows="3"
            required
          />
          {description && <label htmlFor="subcategory-description">Description</label>}
        </div>

        {/* Owners multi-select */}
        <div className="floating-select-form" style={{ width: '99%' }}>
          <Select
            name="owners"
            inputId="subcategory-owners"
            options={ownersOptions}
            value={owners}
            onChange={handleOwnersChange}
            placeholder="Select Owners"
            isMulti
            className={`walkin-form-dropdown ${owners.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            isDisabled={loading}
            styles={{
              control: provided => ({ ...provided, padding: '5px' }),
              placeholder: provided => ({ ...provided, color: 'black' }),
            }}
          />
          {owners.length > 0 && <label htmlFor="subcategory-owners">Owners</label>}
        </div>

        {/* SLAs and Escalation */}
        <div style={{ overflow: 'scroll' }}>
          <fieldset className="enrollment-form-fieldset">
            <legend className="enrollment-form-legend">SLA</legend>
            {slas.map((sla, i) => (
              <div key={i} className="sla-item">
                {/* SLA Duration */}
                <div className="sla-duration-row">
                  <label className="sla-duration-label">SLA Duration:</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="sla-duration-input"
                    value={sla.duration.value}
                    onChange={e => handleSLADurationChange(i, 'value', e.target.value)}
                    required
                    onWheel={e => e.target.blur()}
                    onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                    placeholder='0'
                  />
                  <Select
                    className="dropdown-unit-select"
                    value={durationUnits.find(u => u.value === sla.duration.unit)}
                    onChange={option => handleSLADurationChange(i, 'unit', option.value)}
                    options={durationUnits}
                    classNamePrefix="walkin-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: provided => ({ ...provided, padding: '0' }),
                      placeholder: provided => ({ ...provided, color: 'black' }),
                    }}
                  />
                </div>

                {/* Escalations */}
                <div className="escalation-section">
                  <h5 className="escalation-title">Escalations</h5>
                  {sla.escalations.map((esc, j) => (
                    <div key={j} className="escalation-item">
                      <label className="escalation-level-label">Escalation Level {j + 1}:</label>

                      <label className="escalation-label">Escalation After:</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className="escalation-input"
                        value={esc.duration.value}
                        onChange={e => handleEscDurationChange(i, j, 'value', e.target.value)}
                        placeholder='0'
                        required
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />
                      <Select
                        className="dropdown-unit-select"
                        value={durationUnits.find(u => u.value === esc.duration.unit)}
                        onChange={option => handleEscDurationChange(i, j, 'unit', option.value)}
                        classNamePrefix="walkin-select"
                        options={durationUnits}
                        styles={{
                          control: provided => ({ ...provided, padding: '0' }),
                          placeholder: provided => ({ ...provided, color: 'black' }),
                        }}
                      />
                      <Select
                        name={`escalation-user-${i}-${j}`}
                        inputId={`escalation-user-${i}-${j}`}
                        options={userOptions}
                        value={esc.user}
                        onChange={opt => handleEscUserChange(i, j, opt)}
                        placeholder="Select User"
                        classNamePrefix="walkin-select"
                        menuPortalTarget={document.body}
                        isDisabled={loading}
                        styles={{ placeholder: provided => ({ ...provided, color: 'black' }) }}
                      />
                      <button
                        type="button"
                        className="escalation-remove-btn"
                        onClick={() => removeEscalation(i, j)}
                        disabled={sla.escalations.length === 1}
                        title="Remove escalation"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                      </button>

                      <div className="escalation-trigger-message">
                        {j === 0
                          ? 'This escalation will be triggered after SLA is breached or ticket exceeds SLA duration.'
                          : `This escalation will be triggered after Escalation ${j} is breached.`}
                      </div>
                    </div>
                  ))}
                  <button type="button" className="plusButton" onClick={() => addEscalation(i)} title="Add escalation">
                    <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                      <g mask="url(#mask0_21_345)">
                        <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                      </g>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </fieldset>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? (subcategoryId ? 'Updating…' : 'Creating…') : (subcategoryId ? 'Update SubCategory' : 'Create SubCategory')}
        </button>
      </form>
    </div>
  );
};

export default SubCategoryCreation;
